import { Table, AlloyIcon } from 'cat-ecommerce-alloy';
import Conditional from '@app/components/common/Conditional';
import PropTypes from 'prop-types';
import {
  getCustomRenderers,
  getPartSchema,
  ORIGINAL,
  REPLACEMENT,
  isIndirectReplacement
} from '../utils';
import { PAGE_BREAKPOINTS } from '@app/constants/commonConstants';
import { useBreakpoint } from '@app/hooks';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import cx from 'classnames';
import styles from '../styles.module.scss';

const ReplacementPartsTable = ({ index, item, isAvailabilityToggled }) => {
  const [t] = useTranslation();
  const isLG = useBreakpoint(PAGE_BREAKPOINTS.LG);

  const currency = useSelector(
    s => s.checkout.currentAddToCartModifications.currency
  );
  const isQuantityMergeEnabled = useSelector(
    s => s?.common?.isQuantityMergeEnabled
  );
  const isStoreAvailabilityEnabled = useSelector(
    s => s.dealer?.isStoreAvailabilityEnabled
  );
  const hoseAvailabilityLabel = useSelector(
    s => s.common?.hoseAvailabilityLabel
  );
  const isDeliveryEnabled = useSelector(
    state => state.common.isDeliveryEnabled
  );
  const isPickUpEnabled = useSelector(state => state.dealer.isPickupEnabled);
  const { partName, partNumber, replacementParts, quantity } = item;
  const data = [
    {
      ...item,
      idx: index,
      item,
      unitPrice: item?.price?.formatted?.unitPrice ?? '',
      quantity,
      typeElement: ORIGINAL
    }
  ];

  const newData = replacementParts.map((opt, idx) => ({
    ...opt,
    id: idx,
    item: `${opt.partNumber}: ${opt.partName}`,
    quantity: opt.quantity,
    unitPrice: opt?.price?.formatted?.unitPrice ?? '',
    typeElement: REPLACEMENT,
    rowNotes: (
      <div className="ps-2 pb-1 u-fs--12 u-color--DarkRed">
        {t('INDIRECT_REPLACEMENT_ERROR_MESSAGE')}
      </div>
    ),
    showRowNotes: isIndirectReplacement(opt?.availability)
  }));

  return (
    <div className={cx('mb-3', styles['table-border'])}>
      <div className="px-2 pt-0 pt-lg-2">
        <Conditional test={isLG}>
          <div className="d-flex border-bottom">
            <div className="sequence-indicator">
              <p className="u-color--DarkGray u-fs--16">{`${index})`}</p>
            </div>
            <div className="ps-2 pb-1">
              <p className="text-univers u-color--ExtraDarkGray u-fs--16 mb-0">
                {`${partNumber}: ${partName}`}
              </p>
              <p className="u-color--DarkGray mb-0">
                {t('QUANTITY')} {quantity}
              </p>
              <p className="u-color--DarkGray mb-0">
                {t('AVAILABILITY')}:
                <AlloyIcon
                  height={15}
                  width={15}
                  fill="#E56C00"
                  icon="icon-warning"
                />
                {t('QO_ITEM_UNAVAILABLE')}
              </p>
            </div>
          </div>
        </Conditional>
        <div className="ps-0 ps-lg-4">
          <Table
            keyGenerator={p => `${p.partNumber}${p.sosCode}`}
            schema={getPartSchema(
              currency,
              t,
              isQuantityMergeEnabled,
              isDeliveryEnabled,
              isPickUpEnabled
            )}
            data={isLG ? newData : data}
            customRenderers={getCustomRenderers(
              currency,
              replacementParts,
              isStoreAvailabilityEnabled,
              isAvailabilityToggled,
              hoseAvailabilityLabel
            )}
          />
        </div>
      </div>
    </div>
  );
};

ReplacementPartsTable.propTypes = {
  index: PropTypes.string,
  isAvailabilityToggled: PropTypes.bool,
  item: PropTypes.shape({
    replacementParts: PropTypes.arrayOf(
      PropTypes.shape({
        partName: PropTypes.string,
        partNumber: PropTypes.string,
        quantity: PropTypes.number,
        price: PropTypes.shape({
          formatted: PropTypes.shape({
            coreCharges: PropTypes.string,
            discountAmount: PropTypes.string,
            discountedPrice: PropTypes.string,
            totalPrice: PropTypes.string,
            undiscountedPrice: PropTypes.string,
            unitPrice: PropTypes.string
          }),
          unformatted: PropTypes.shape({
            coreCharges: PropTypes.number,
            discountAmount: PropTypes.number,
            discountedPrice: PropTypes.number,
            totalPrice: PropTypes.number,
            undiscountedPrice: PropTypes.number,
            unitPrice: PropTypes.number
          })
        }),
        availability: PropTypes.arrayOf(
          PropTypes.shape({
            availabilityMessage: PropTypes.string,
            quantityAvailable: PropTypes.string
          })
        ),
        replacementMessage: PropTypes.string,
        sosCode: PropTypes.string,
        weight: PropTypes.string
      })
    ),
    partName: PropTypes.string,
    partNumber: PropTypes.string,
    quantity: PropTypes.number,
    sosCode: PropTypes.string
  })
};

export default ReplacementPartsTable;
